import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
	AppBar,
	Box,
	Typography,
	InputBase,
	Popper,
	Paper,
	ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StatusPill from 'features/IRWorkstation/components/StatusPill';
import fakeData from 'features/IRWorkstation/constants/fakeData';

const Navbar = ({
	currentPage,
	setCurrentPage,
	visitablePages,
	onOpenLP,
	onOpenExplainer,
}) => {
	const [lpOpen, setLpOpen] = useState(false);
	const [lpSearch, setLpSearch] = useState('');
	const lpAnchor = useRef(null);

	const filtered = fakeData.lpOptions.filter((lp) =>
		lp.name.toLowerCase().includes(lpSearch.toLowerCase())
	);

	const handleSelectLP = (lp) => {
		setLpOpen(false);
		setLpSearch('');
		onOpenLP(lp);
	};

	return (
		<AppBar
			position='static'
			sx={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				px: '18px',
				gap: '2px',
			}}
		>
			{/* logo */}
			<Typography
				sx={{
					fontSize: '13px',
					fontWeight: 600,
					color: 'text.primary',
					mr: 3,
					whiteSpace: 'nowrap',
				}}
			>
				IR Workstation
			</Typography>
			{/* divider */}
			<Box
				sx={{
					width: '1px',
					height: 16,
					backgroundColor: 'border.light',
					mx: '10px',
					flexShrink: 0,
				}}
			/>

			{/* tabs */}
			{Object.entries(visitablePages).map(([key, label]) => {
				const isActive = currentPage === key;
				return (
					<Box
						key={key}
						onClick={() => setCurrentPage(key)}
						sx={{
							padding: '6px 11px',
							borderRadius: '6px',
							display: 'flex',
							alignItems: 'center',
							cursor: 'pointer',
							backgroundColor: isActive ? 'background.subtle' : 'transparent',
							'&:hover': {
								backgroundColor: 'background.subtle',
								'& .nav-tab-label': { fontWeight: 600, color: 'text.primary' },
							},
						}}
					>
						<Typography
							sx={{
								fontSize: '12px',
								fontWeight: isActive ? 700 : 600,
								color: isActive ? 'text.primary' : 'text.secondary',
								whiteSpace: 'nowrap',
								// hidden always-bold label - makes sure the element reserves enough space for
								// the hover state to not shift content when it changes the font weight
								'&::after': {
									content: `"${label}"`,
									display: 'block',
									fontWeight: 600,
									height: 0,
									overflow: 'hidden',
									visibility: 'hidden',
								},
							}}
							className='nav-tab-label'
						>
							{label}
						</Typography>
					</Box>
				);
			})}

			{/* vdr tab */}
			<Box
				sx={{
					padding: '6px 13px',
					borderRadius: '6px',
					display: 'flex',
					alignItems: 'center',
					cursor: 'pointer',
					backgroundColor: 'vdr.bg',
					border: '0.5px solid',
					borderColor: 'vdr.border',
					'&:hover': { backgroundColor: 'vdr.hover' },
				}}
			>
				<Typography
					sx={{
						fontSize: '12px',
						fontWeight: 600,
						color: 'vdr.text',
						whiteSpace: 'nowrap',
					}}
				>
					↔ Raise VDR
				</Typography>
			</Box>

			{/* spacer */}
			<Box sx={{ flex: 1 }} />

			{/* lp lookup */}
			<ClickAwayListener onClickAway={() => setLpOpen(false)}>
				<Box sx={{ position: 'relative' }}>
					<Box
						ref={lpAnchor}
						onClick={() => setLpOpen((o) => !o)}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 29,
							height: 29,
							borderRadius: '6px',
							border: '1px solid',
							borderColor: 'border.main',
							cursor: 'pointer',
							backgroundColor: 'background.subtle',
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<SearchIcon sx={{ fontSize: 16, color: 'text.primary' }} />
					</Box>
					<Popper
						open={lpOpen}
						anchorEl={lpAnchor.current}
						placement='bottom-end'
						sx={{ zIndex: 1300, mt: '4px' }}
					>
						<Paper
							sx={{
								width: 290,
								maxHeight: 360,
								overflow: 'hidden',
								display: 'flex',
								flexDirection: 'column',
								border: '0.5px solid',
								borderColor: 'border.main',
								borderRadius: '8px',
							}}
						>
							<Box
								sx={{
									padding: '8px',
									borderBottom: '0.5px solid',
									borderColor: 'border.light',
									backgroundColor: 'background.subtle',
								}}
							>
								<InputBase
									autoFocus
									placeholder='Search LPs…'
									value={lpSearch}
									onChange={(e) => setLpSearch(e.target.value)}
									sx={{ fontSize: '12px', width: '100%', px: 1 }}
								/>
							</Box>
							<Box sx={{ overflowY: 'auto', flex: 1 }}>
								{filtered.map((lp) => (
									<Box
										key={lp.name}
										onClick={() => handleSelectLP(lp)}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											padding: '7px 10px',
											cursor: 'pointer',
											'&:hover': { backgroundColor: 'background.subtle' },
											borderBottom: '0.5px solid',
											borderColor: 'border.light',
										}}
									>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
												{lp.name}
											</Typography>
											<Typography
												sx={{ fontSize: '10px', color: 'text.tertiary' }}
											>
												{lp.type} · {lp.tier}
											</Typography>
										</Box>
										<StatusPill
											label={lp.health === 'At Risk' ? 'At Risk' : lp.reup}
											color={
												lp.reup === 'High'
													? 'green'
													: lp.health === 'At Risk'
														? 'red'
														: lp.reup === 'Medium'
															? 'amber'
															: 'gray'
											}
										/>
									</Box>
								))}
								{filtered.length === 0 && (
									<Typography
										sx={{
											padding: 2,
											fontSize: '12px',
											color: 'text.tertiary',
											textAlign: 'center',
										}}
									>
										No LPs found
									</Typography>
								)}
							</Box>
						</Paper>
					</Popper>
				</Box>
			</ClickAwayListener>

			{/* explain this page */}
			<Box
				onClick={onOpenExplainer}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '5px',
					padding: '5px 12px',
					borderRadius: '6px',
					backgroundColor: 'status.blue.bg',
					color: 'status.blue.text',
					border: '0.5px solid',
					borderColor: 'primary.main',
					cursor: 'pointer',
					ml: 2,
					boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
					'&:hover': { backgroundColor: 'status.blue.hover' },
				}}
			>
				<Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
					? Explain this page
				</Typography>
			</Box>
		</AppBar>
	);
};

Navbar.propTypes = {
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	visitablePages: PropTypes.object.isRequired,
	onOpenLP: PropTypes.func.isRequired,
	onOpenExplainer: PropTypes.func.isRequired,
};

export default Navbar;
